/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * InquiryInfo Page component
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.15.0
 */

import { Fragment, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Title from '@components/common/Title/Title';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPageInquiryInfoTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { Loader } from '@modules/loader/components/Loader';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import type { IRouteParams } from '@routes/constants/routesConstants';
import { icons as themeIcons } from '@theme/index';

import ErrorPage from '../../ErrorPage/ErrorPage';
import { useGetInquiryInfoGeneralQuery, useSendInquiryViewedMutation } from '../store';
import InquiryInfoPageAttachedFiles from './InquiryInfoPageAttachedFiles';
import InquiryInfoPageChatHistory from './InquiryInfoPageChatHistory';
import InquiryInfoPageCommonInfo from './InquiryInfoPageCommonInfo';
import InquiryInfoPageCustomAttributes from './InquiryInfoPageCustomAttributes';
import { InquiryInfoPageContentStyled, InquiryInfoPageStyled } from './styled/InquiryInfoPageStyled';

const InquiryInfoPage = () => {
    const dispatch = useDispatch();
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationInquiryInfo = useSelector(makeSelectPageInquiryInfoTranslation());
    const [isShowLoader, setIsShowLoader] = useState(false);

    const { inquiryId } = useParams<IRouteParams>();

    const { data, isFetching, isLoading, isSuccess, error } = useGetInquiryInfoGeneralQuery(
        { inquiryId },
        {
            skip: !inquiryId,
        },
    );

    const [sendInquiryViewed] = useSendInquiryViewedMutation();

    useEffect(() => {
        if (isLoading || !inquiryId) return;
        dispatch(
            logBusinessEvent({
                eventName: ELogBusinessEventName.B2B_SHOW_INQUIRY_INFO,
                type: ELogBusinessEventType.common,
            }),
        );
    }, [dispatch, inquiryId, isLoading]);

    useEffect(() => {
        if (inquiryId && !isFetching && isSuccess && data) {
            sendInquiryViewed({ inquiryId });
        }
    }, [data, inquiryId, isFetching, isSuccess, sendInquiryViewed]);

    return (
        <Fragment>
            <PageLocale
                lazy
                fileNames={[
                    TranslationFileNamesEnum.common,
                    TranslationFileNamesEnum.corporate,
                    TranslationFileNamesEnum.pageInquiryInfo,
                ]}
            />

            <Title pageName={ECommonTranslationPages.inquiryInfo} />

            <div>
                <Loader showLoader={isFetching || isShowLoader}>
                    {error || (isSuccess && !data) ? (
                        <ErrorPage
                            src={themeIcons.errorDeliveryImage3x}
                            text={translationCommon.error.ui.text}
                            title={translationCommon.error.ui.title}
                        />
                    ) : (
                        !isFetching && (
                            <InquiryInfoPageStyled>
                                {/*Основная информация*/}
                                <InquiryInfoPageCommonInfo
                                    inquiryId={inquiryId}
                                    translationCommon={translationCommon}
                                    translationInquiryInfo={translationInquiryInfo}
                                />
                                {/*Кастомные атрибуты*/}
                                <InquiryInfoPageCustomAttributes
                                    inquiryId={inquiryId}
                                    translationInquiryInfo={translationInquiryInfo}
                                />
                                <div className="inquiry-info-page__text">
                                    {!data?.hasInformationForUser && translationInquiryInfo.informationForClient}
                                </div>

                                {/*История переписки и Приложенные файлы*/}
                                {!isFetching && !isShowLoader && (
                                    <InquiryInfoPageContentStyled>
                                        <InquiryInfoPageChatHistory
                                            allowedFileExtensions={data?.allowedFileExtensions}
                                            inquiryId={inquiryId}
                                            maxFileCount={data?.maxFileCount}
                                            setIsShowLoader={setIsShowLoader}
                                            translationInquiryInfo={translationInquiryInfo}
                                        />
                                        <InquiryInfoPageAttachedFiles
                                            files={data?.inquiry.files || []}
                                            filesCount={data?.inquiry.filesCount || 0}
                                            inquiryId={inquiryId}
                                            translationInquiryInfo={translationInquiryInfo}
                                        />
                                    </InquiryInfoPageContentStyled>
                                )}
                            </InquiryInfoPageStyled>
                        )
                    )}
                </Loader>
            </div>
        </Fragment>
    );
};

export default InquiryInfoPage;
